import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useParams } from "@/lib/router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type Connection,
  type Node,
  type Edge,
  type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { graphsApi, type AgentGraph } from "../api/graphs";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useGraphRun } from "../hooks/useGraphRun";
import { useGraphValidation } from "../hooks/useGraphValidation";
import { queryKeys } from "../lib/queryKeys";
import { PageSkeleton } from "../components/PageSkeleton";
import {
  AgentFlowNode,
  ToolFlowNode,
  StartFlowNode,
  EndFlowNode,
  InterruptFlowNode,
  RouterFlowNode,
  TriggerFlowNode,
} from "../components/studio/FlowNodes";
import { NodeConfigDrawer } from "../components/studio/NodeConfigDrawer";
import { EdgeConfigDrawer } from "../components/studio/EdgeConfigDrawer";
import { Palette, getDefaultData } from "../components/studio/Palette";
import { InteractionPanel } from "../components/studio/InteractionPanel";
import { ExecutionHistory } from "../components/studio/ExecutionHistory";
import { InterruptDialog } from "../components/studio/InterruptDialog";
import { ArtifactPanel, type Artifact } from "../components/studio/ArtifactPanel";
import { BuildAgentPanel } from "../components/studio/BuildAgentPanel";
import { Button } from "@/components/ui/button";
import {
  Save,
  Play,
  Maximize,
  History,
  MessageSquare,
  Workflow,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileText,
  Rocket,
  Square,
  Sparkles,
} from "lucide-react";
import { cn } from "../lib/utils";

const nodeTypes: NodeTypes = {
  agent: AgentFlowNode,
  tool: ToolFlowNode,
  start: StartFlowNode,
  end: EndFlowNode,
  interrupt: InterruptFlowNode,
  router: RouterFlowNode,
  trigger: TriggerFlowNode,
};

type Tab = "graph" | "interact" | "history" | "artifacts" | "build";

const NODE_LEGEND = [
  { label: "Agent", color: "bg-violet-500" },
  { label: "Tool", color: "bg-emerald-500" },
  { label: "Router", color: "bg-amber-500" },
  { label: "Trigger", color: "bg-blue-500" },
  { label: "Interrupt", color: "bg-orange-500" },
  { label: "Start/End", color: "bg-zinc-400" },
];

let nodeIdCounter = 0;
function generateNodeId(type: string) {
  nodeIdCounter += 1;
  return `${type}_${nodeIdCounter}`;
}

function AgentBuilderInner() {
  const { graphId } = useParams<{ graphId: string }>();
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const reactFlowInstance = useReactFlow();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("graph");
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(400);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [interruptEvent, setInterruptEvent] = useState<{
    nodeId: string;
    nodeName: string;
    reason?: string;
    state: Record<string, unknown>;
  } | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const { data: graph, isLoading } = useQuery({
    queryKey: queryKeys.graphs.detail(graphId!),
    queryFn: () => graphsApi.get(graphId!),
    enabled: !!graphId,
  });

  const initialNodes = useMemo(() => {
    if (!graph?.graphDefinition?.nodes) return [];
    return graph.graphDefinition.nodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: n.data,
    }));
  }, [graph]);

  const initialEdges = useMemo(() => {
    if (!graph?.graphDefinition?.edges) return [];
    return graph.graphDefinition.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      data: { condition: e.condition },
      animated: true,
      style: { stroke: "var(--primary)", strokeWidth: 2 },
    }));
  }, [graph]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const graphRun = useGraphRun(selectedPracticeId);
  const validation = useGraphValidation(nodes, edges);

  useEffect(() => {
    if (initialNodes.length > 0) setNodes(initialNodes);
  }, [initialNodes, setNodes]);
  useEffect(() => {
    if (initialEdges.length > 0) setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Agent Studio", href: "/studio" },
      { label: graph?.name ?? "Loading..." },
    ]);
  }, [setBreadcrumbs, graph?.name]);

  // ── Execution highlighting ─────────────────────────────────────
  useEffect(() => {
    if (graphRun.status === "running" && activeTab === "graph") {
      setActiveTab("interact");
    }
  }, [graphRun.status, activeTab]);

  // Detect interrupt events
  useEffect(() => {
    const lastEvent = graphRun.events[graphRun.events.length - 1];
    if (lastEvent?.type === "interrupt" && lastEvent.nodeId) {
      const node = nodes.find((n) => n.id === lastEvent.nodeId);
      setInterruptEvent({
        nodeId: lastEvent.nodeId,
        nodeName: (node?.data as Record<string, unknown>)?.name as string ?? lastEvent.nodeId,
        reason: lastEvent.data?.reason as string | undefined,
        state: (lastEvent.data ?? {}) as Record<string, unknown>,
      });
    }
  }, [graphRun.events, nodes]);

  // Collect artifacts from execution events
  useEffect(() => {
    if (graphRun.status === "idle") {
      setArtifacts([]);
      return;
    }
    const newArtifacts: Artifact[] = [];
    for (const event of graphRun.events) {
      if (event.type === "artifact" && event.data) {
        const node = nodes.find((n) => n.id === event.nodeId);
        newArtifacts.push({
          id: (event.data.id as string) ?? `artifact-${newArtifacts.length}`,
          title: (event.data.title as string) ?? "Untitled Document",
          type: (event.data.type as Artifact["type"]) ?? "markdown",
          content: (event.data.content as string) ?? "",
          nodeId: event.nodeId,
          nodeName: (node?.data as Record<string, unknown>)?.name as string ?? event.nodeId,
          timestamp: event.timestamp,
        });
      }
    }
    setArtifacts(newArtifacts);
  }, [graphRun.events, graphRun.status, nodes]);

  // Track active/completed/error nodes from SSE events
  useEffect(() => {
    if (graphRun.status === "idle") {
      // Clear all execution state
      setNodes((nds) =>
        nds.map((n) => {
          if ((n.data as Record<string, unknown>).__execState) {
            const { __execState, ...rest } = n.data as Record<string, unknown>;
            return { ...n, data: rest };
          }
          return n;
        }),
      );
      return;
    }

    const activeNodes = new Set<string>();
    const completedNodes = new Set<string>();
    const errorNodes = new Set<string>();

    for (const event of graphRun.events) {
      if (event.type === "node_start" && event.nodeId) {
        activeNodes.add(event.nodeId);
        completedNodes.delete(event.nodeId);
      }
      if (event.type === "node_end" && event.nodeId) {
        activeNodes.delete(event.nodeId);
        completedNodes.add(event.nodeId);
      }
      if (event.type === "error" && event.nodeId) {
        activeNodes.delete(event.nodeId);
        errorNodes.add(event.nodeId);
      }
    }

    setNodes((nds) =>
      nds.map((n) => {
        let state: string | undefined;
        if (errorNodes.has(n.id)) state = "error";
        else if (activeNodes.has(n.id)) state = "active";
        else if (completedNodes.has(n.id)) state = "completed";

        const current = (n.data as Record<string, unknown>).__execState;
        if (current === state) return n;
        return { ...n, data: { ...n.data, __execState: state } };
      }),
    );
  }, [graphRun.events, graphRun.status, setNodes]);

  // Animate edges during runs
  useEffect(() => {
    if (graphRun.status === "running") {
      setEdges((eds) =>
        eds.map((e) => ({
          ...e,
          animated: true,
          style: { ...e.style, strokeDasharray: "5 5" },
        })),
      );
    } else if (graphRun.status === "succeeded" || graphRun.status === "failed") {
      setEdges((eds) =>
        eds.map((e) => ({
          ...e,
          animated: false,
          style: { stroke: "var(--primary)", strokeWidth: 2 },
        })),
      );
    }
  }, [graphRun.status, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            style: { stroke: "var(--primary)", strokeWidth: 2 },
          },
          eds,
        ),
      );
    },
    [setEdges],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      graphsApi.update(graphId!, {
        graphDefinition: {
          nodes: nodes.map((n) => ({
            id: n.id,
            type: n.type as "agent" | "tool" | "router" | "start" | "end" | "interrupt" | "trigger",
            position: n.position,
            data: n.data as Record<string, unknown>,
          })),
          edges: edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            label: typeof e.label === "string" ? e.label : undefined,
            condition: (e.data as Record<string, unknown>)?.condition as string | undefined,
          })),
          config: graph?.graphDefinition?.config ?? {},
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.detail(graphId!) });
    },
  });

  const isDeployed = graph?.status === "deployed";

  const deployMutation = useMutation({
    mutationFn: () => graphsApi.deploy(graphId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.detail(graphId!) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.list(selectedPracticeId!) });
    },
  });

  const undeployMutation = useMutation({
    mutationFn: () => graphsApi.undeploy(graphId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.detail(graphId!) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.list(selectedPracticeId!) });
    },
  });

  // Drag and drop from palette
  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow-type");
      if (!type) return;

      const dataStr = event.dataTransfer.getData("application/reactflow-data");
      const data = dataStr ? JSON.parse(dataStr) : {};
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: generateNodeId(type),
        type: type as "agent" | "tool" | "router" | "start" | "end" | "interrupt",
        position,
        data,
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, setNodes],
  );

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null;
  const selectedEdge = edges.find((e) => e.id === selectedEdgeId) ?? null;

  const updateNodeData = useCallback(
    (nodeId: string, data: Record<string, unknown>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n)),
      );
    },
    [setNodes],
  );

  const updateEdgeData = useCallback(
    (edgeId: string, data: { label?: string; condition?: string }) => {
      setEdges((eds) =>
        eds.map((e) => {
          if (e.id !== edgeId) return e;
          return {
            ...e,
            label: data.label !== undefined ? data.label : e.label,
            data: { ...(e.data as Record<string, unknown>), condition: data.condition },
          };
        }),
      );
    },
    [setEdges],
  );

  const deleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setSelectedEdgeId(null);
    },
    [setEdges],
  );

  // Resizable divider
  const onDividerMouseDown = useCallback(() => {
    setIsDraggingDivider(true);
  }, []);

  useEffect(() => {
    if (!isDraggingDivider) return;
    const onMouseMove = (e: MouseEvent) => {
      const container = document.getElementById("agent-builder-container");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newWidth = Math.max(280, Math.min(600, rect.right - e.clientX));
      setRightPanelWidth(newWidth);
    };
    const onMouseUp = () => setIsDraggingDivider(false);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDraggingDivider]);

  if (isLoading) return <PageSkeleton variant="detail" />;

  return (
    <div id="agent-builder-container" className="flex flex-col h-[calc(100vh-7rem)] -m-4 md:-m-6">
      {/* Tab Bar */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-border bg-card/50 shrink-0 overflow-x-auto scrollbar-auto-hide">
        <button
          type="button"
          onClick={() => setActiveTab("graph")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            activeTab === "graph"
              ? "bg-primary/10 text-primary border border-primary/30"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )}
        >
          <Workflow className="h-3.5 w-3.5" />
          Visual Builder
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("interact")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            activeTab === "interact"
              ? "bg-primary/10 text-primary border border-primary/30"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Interact
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("history")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            activeTab === "history"
              ? "bg-primary/10 text-primary border border-primary/30"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )}
        >
          <History className="h-3.5 w-3.5" />
          Execution History
          {graphRun.events.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/20 text-primary text-[9px] tabular-nums">
              {graphRun.events.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("artifacts")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            activeTab === "artifacts"
              ? "bg-primary/10 text-primary border border-primary/30"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )}
        >
          <FileText className="h-3.5 w-3.5" />
          Artifacts
          {artifacts.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/20 text-primary text-[9px] tabular-nums">
              {artifacts.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("build")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            activeTab === "build"
              ? "bg-primary/10 text-primary border border-primary/30"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )}
        >
          <Sparkles className="h-3.5 w-3.5" />
          Build
        </button>

        {/* Validation indicator */}
        <div className="relative ml-2">
          <button
            type="button"
            onClick={() => setShowValidation(!showValidation)}
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
              validation.hasErrors
                ? "text-destructive bg-destructive/10"
                : validation.hasWarnings
                  ? "text-amber-600 bg-amber-100/50 dark:text-amber-400 dark:bg-amber-900/20"
                  : "text-emerald-600 bg-emerald-100/50 dark:text-emerald-400 dark:bg-emerald-900/20",
            )}
          >
            {validation.hasErrors ? (
              <XCircle className="h-3 w-3" />
            ) : validation.hasWarnings ? (
              <AlertTriangle className="h-3 w-3" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            {validation.issues.length > 0 ? validation.issues.length : "Valid"}
          </button>
          {showValidation && validation.issues.length > 0 && (
            <div className="absolute top-full left-0 mt-1 w-72 rounded-lg border border-border bg-card shadow-lg z-50 p-2 space-y-1">
              {validation.issues.map((issue, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex items-start gap-2 px-2 py-1.5 rounded text-[11px]",
                    issue.level === "error" ? "text-destructive bg-destructive/5" : "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10",
                  )}
                >
                  {issue.level === "error" ? <XCircle className="h-3 w-3 mt-0.5 shrink-0" /> : <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />}
                  {issue.message}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right-side toolbar */}
        <div className="ml-auto flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => reactFlowInstance.fitView()}
            className="h-7 text-[11px]"
          >
            <Maximize className="h-3 w-3 mr-1" />
            Fit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="h-7 text-[11px]"
          >
            <Save className="h-3 w-3 mr-1" />
            {saveMutation.isPending ? "Saving..." : "Save"}
          </Button>
          <Button
            size="sm"
            onClick={() => graphId && graphRun.run(graphId)}
            disabled={graphRun.status === "running" || validation.hasErrors}
            className="h-7 text-[11px]"
            title={validation.hasErrors ? "Fix validation errors before running" : undefined}
          >
            <Play className="h-3 w-3 mr-1" />
            {graphRun.status === "running" ? "Running..." : "Run"}
          </Button>
          {graphId && (
            <Button
              size="sm"
              variant={isDeployed ? "destructive" : "default"}
              onClick={() => {
                if (isDeployed) {
                  undeployMutation.mutate();
                } else {
                  // Save first, then deploy
                  saveMutation.mutateAsync().then(() => deployMutation.mutate());
                }
              }}
              disabled={deployMutation.isPending || undeployMutation.isPending || validation.hasErrors}
              className="h-7 text-[11px]"
              title={isDeployed ? "Stop this agent and return to draft" : "Deploy this graph as a production agent"}
            >
              {isDeployed ? (
                <>
                  <Square className="h-3 w-3 mr-1" />
                  {undeployMutation.isPending ? "Stopping..." : "Undeploy"}
                </>
              ) : (
                <>
                  <Rocket className="h-3 w-3 mr-1" />
                  {deployMutation.isPending ? "Deploying..." : "Deploy"}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Palette (graph tab only) */}
        {activeTab === "graph" && (
          <Palette collapsed={paletteCollapsed} onToggle={() => setPaletteCollapsed(!paletteCollapsed)} />
        )}

        {/* Canvas / Tab content */}
        <div className="flex-1 relative min-w-0">
          {activeTab === "graph" && (
            <>
              <div className="h-full" ref={reactFlowWrapper}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  nodeTypes={nodeTypes}
                  onNodeClick={(_event, node) => {
                    setSelectedNodeId(node.id);
                    setSelectedEdgeId(null);
                  }}
                  onEdgeClick={(_event, edge) => {
                    setSelectedEdgeId(edge.id);
                    setSelectedNodeId(null);
                  }}
                  onPaneClick={() => {
                    setSelectedNodeId(null);
                    setSelectedEdgeId(null);
                    setShowValidation(false);
                  }}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  fitView
                  connectionLineStyle={{ stroke: "var(--primary)", strokeWidth: 2 }}
                  proOptions={{ hideAttribution: true }}
                >
                  <Controls className="!bg-card !border-border !shadow-sm" />
                  <MiniMap
                    className="!bg-card !border-border"
                    nodeColor="var(--primary)"
                    maskColor="color-mix(in oklch, var(--background) 70%, transparent)"
                  />
                  <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="color-mix(in oklch, var(--muted-foreground) 15%, transparent)" />
                </ReactFlow>
              </div>

              {/* Node legend */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-4 px-4 py-1.5 rounded-full bg-card/90 backdrop-blur border border-border/50 shadow-sm">
                {NODE_LEGEND.map((item) => (
                  <div key={item.label} className="flex items-center gap-1.5">
                    <div className={cn("h-2.5 w-2.5 rounded-full", item.color)} />
                    <span className="text-[10px] text-muted-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === "interact" && (
            <InteractionPanel
              events={graphRun.events}
              status={graphRun.status}
              error={graphRun.error}
              onSubmit={(msg) => graphId && graphRun.run(graphId, { messages: [{ role: "user", content: msg }] })}
              onStop={() => graphRun.stop()}
              nodes={nodes}
            />
          )}

          {activeTab === "history" && graphId && selectedPracticeId && (
            <ExecutionHistory graphId={graphId} companyId={selectedPracticeId} />
          )}

          {activeTab === "artifacts" && (
            <ArtifactPanel
              artifacts={artifacts}
              onUpdate={(id, content) => {
                setArtifacts((prev) =>
                  prev.map((a) => (a.id === id ? { ...a, content } : a)),
                );
              }}
            />
          )}

          {activeTab === "build" && (
            graphId ? (
              <BuildAgentPanel
                graphId={graphId}
                nodes={nodes as any}
                edges={edges as any}
                setNodes={setNodes as any}
                setEdges={setEdges as any}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-center p-8">
                <p className="text-sm font-medium text-foreground">No graph selected</p>
                <p className="text-xs text-muted-foreground max-w-xs">Open an agent graph from the sidebar to start using the Build Agent.</p>
              </div>
            )
          )}
        </div>

        {/* Config Drawer — Node */}
        {activeTab === "graph" && selectedNode && !selectedEdge && (
          <NodeConfigDrawer
            node={selectedNode}
            onUpdate={(data) => updateNodeData(selectedNode.id, data)}
            onClose={() => setSelectedNodeId(null)}
          />
        )}

        {/* Config Drawer — Edge */}
        {activeTab === "graph" && selectedEdge && (
          <EdgeConfigDrawer
            edge={{
              id: selectedEdge.id,
              label: typeof selectedEdge.label === "string" ? selectedEdge.label : undefined,
              data: selectedEdge.data as Record<string, unknown>,
            }}
            onUpdate={(data) => updateEdgeData(selectedEdge.id, data)}
            onDelete={() => deleteEdge(selectedEdge.id)}
            onClose={() => setSelectedEdgeId(null)}
          />
        )}

        {/* Right trace panel (graph tab, shown during/after runs) */}
        {activeTab === "graph" && graphRun.status !== "idle" && (
          <>
            <div
              className="w-1 shrink-0 cursor-col-resize bg-border/50 hover:bg-primary/30 active:bg-primary/50 transition-colors"
              onMouseDown={onDividerMouseDown}
            />
            <div style={{ width: rightPanelWidth }} className="shrink-0 border-l border-border">
              <InteractionPanel
                events={graphRun.events}
                status={graphRun.status}
                error={graphRun.error}
                onSubmit={(msg) => graphId && graphRun.run(graphId, { messages: [{ role: "user", content: msg }] })}
                onStop={() => graphRun.stop()}
                nodes={nodes}
              />
            </div>
          </>
        )}
      </div>

      {/* HITL Interrupt Dialog */}
      {interruptEvent && (
        <InterruptDialog
          nodeId={interruptEvent.nodeId}
          nodeName={interruptEvent.nodeName}
          reason={interruptEvent.reason}
          state={interruptEvent.state}
          onApprove={() => {
            setInterruptEvent(null);
            // TODO: POST resume with approval
          }}
          onReject={(reason) => {
            setInterruptEvent(null);
            // TODO: POST resume with rejection
          }}
          onEdit={(editedState) => {
            setInterruptEvent(null);
            // TODO: POST resume with edited state
          }}
          onDismiss={() => setInterruptEvent(null)}
        />
      )}
    </div>
  );
}

export function AgentBuilder() {
  return (
    <ReactFlowProvider>
      <AgentBuilderInner />
    </ReactFlowProvider>
  );
}
